import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../hooks/useTheme';
import UserAvatar from '../../shared/UserAvatar';
import LocationMap from '../../shared/LocationMap';

interface MoodBoardPreviewCardProps {
  moodBoard: {
    id: string | number;
    name: string;
    description?: string;
    location?: string;
    images: string[];
    colors: string[];
    tags: string[];
    is_public?: boolean;
    user_name?: string;
    user_profile_image?: string;
    likes_count: number;
    created_at: string;
    is_liked?: boolean;
  };
  onLike?: () => void;
  onShare?: () => void;
  onPress?: () => void;
  showActions?: boolean;
}

const MoodBoardPreviewCard: React.FC<MoodBoardPreviewCardProps> = ({
  moodBoard,
  onLike,
  onShare,
  onPress,
  showActions = true,
}) => {
  const { colors } = useTheme();

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
    return `${Math.floor(diffInSeconds / 604800)}w`;
  };

  const mockEmail = moodBoard.user_name
    ? `${moodBoard.user_name}@example.com`
    : 'user@example.com';

  return (
    <TouchableOpacity
      style={[styles.feedCard, { backgroundColor: colors.card }]}
      onPress={onPress}
      activeOpacity={0.95}
      disabled={!onPress}
    >
      {/* Card Header */}
      <View style={styles.cardHeader}>
        <View style={styles.userSection}>
          <UserAvatar
            size={32}
            imageUrl={moodBoard.user_profile_image}
            name={moodBoard.user_name}
            email={mockEmail}
            showActiveStatus={true}
            isActive={true}
          />
          <View style={styles.userInfo}>
            <Text style={[styles.username, { color: colors.text }]}>
              {moodBoard.user_name || 'Color Explorer'}
            </Text>
            <Text style={[styles.timestamp, { color: colors.secondary }]}>
              {formatTimeAgo(moodBoard.created_at)}
            </Text>
          </View>
        </View>

        {/* Privacy indicator */}
        {moodBoard.is_public !== undefined && (
          <View
            style={[
              styles.privacyBadge,
              {
                backgroundColor: moodBoard.is_public
                  ? 'rgba(34, 197, 94, 0.1)'
                  : 'rgba(107, 114, 128, 0.1)',
              },
            ]}
          >
            <Ionicons
              name={moodBoard.is_public ? 'globe-outline' : 'lock-closed'}
              size={12}
              color={moodBoard.is_public ? '#22C55E' : '#6B7280'}
            />
            <Text
              style={[
                styles.privacyText,
                { color: moodBoard.is_public ? '#22C55E' : '#6B7280' },
              ]}
            >
              {moodBoard.is_public ? 'Public' : 'Private'}
            </Text>
          </View>
        )}
      </View>

      {/* Card Content */}
      <View style={styles.cardContent}>
        <Text style={[styles.cardTitle, { color: colors.text }]}>
          {moodBoard.name}
        </Text>

        {moodBoard.description && (
          <Text style={[styles.cardDescription, { color: colors.secondary }]}>
            {moodBoard.description}
          </Text>
        )}
        {moodBoard.location && (
          <>
            <View style={styles.locationRow}>
              <Ionicons
                name="location-outline"
                size={14}
                color={colors.secondary}
              />
              <Text style={[styles.locationText, { color: colors.secondary }]}>
                {moodBoard.location}
              </Text>
            </View>
            <LocationMap
              location={moodBoard.location}
              height={150}
              showControls={false}
            />
          </>
        )}
      </View>

      {/* Visual Content */}
      {moodBoard.images && moodBoard.images.length > 0 && (
        <View style={styles.visualContent}>
          <View style={styles.imageGrid}>
            {moodBoard.images
              .slice(0, 4)
              .map((image: string, imageIndex: number) => (
                <View key={imageIndex} style={styles.imageContainer}>
                  <Image
                    source={{ uri: image }}
                    style={styles.feedImage}
                    resizeMode="cover"
                  />
                  {imageIndex === 3 && moodBoard.images.length > 4 && (
                    <View style={styles.moreImagesOverlay}>
                      <Text style={styles.moreImagesText}>
                        +{moodBoard.images.length - 4}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
          </View>
        </View>
      )}

      {/* Color Palette */}
      {moodBoard.colors && moodBoard.colors.length > 0 && (
        <View style={styles.visualContent}>
          <View
            style={[
              styles.colorPalette,
              {
                width:
                  moodBoard.images && moodBoard.images.length > 0
                    ? `${Math.min(moodBoard.images.length, 4) * 24 + 0.4}%`
                    : '96%',
              },
            ]}
          >
            {moodBoard.colors.map((color: string, colorIndex: number) => (
              <View
                key={colorIndex}
                style={[styles.colorSwatch, { backgroundColor: color }]}
              >
                <Text style={styles.colorHex}>{color}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Tags */}
      {moodBoard.tags && moodBoard.tags.length > 0 && (
        <View style={styles.tagsContainer}>
          {moodBoard.tags.slice(0, 3).map((tag: string, tagIndex: number) => (
            <View
              key={tagIndex}
              style={[styles.tag, { backgroundColor: colors.primary + '15' }]}
            >
              <Text style={[styles.tagText, { color: colors.primary }]}>
                #{tag}
              </Text>
            </View>
          ))}
          {moodBoard.tags.length > 3 && (
            <Text style={[styles.moreTagsText, { color: colors.secondary }]}>
              +{moodBoard.tags.length - 3}
            </Text>
          )}
        </View>
      )}

      {/* Engagement Actions */}
      {showActions && (
        <View style={[styles.engagementBar, { borderTopColor: colors.border }]}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={onLike}
            activeOpacity={0.7}
          >
            <Ionicons
              name={moodBoard.is_liked ? 'heart' : 'heart-outline'}
              size={18}
              color={moodBoard.is_liked ? '#FF6B6B' : colors.secondary}
            />
            <Text style={[styles.actionText, { color: colors.secondary }]}>
              {moodBoard.likes_count}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={onShare}
            activeOpacity={0.7}
          >
            <Ionicons name="share-outline" size={18} color={colors.secondary} />
            <Text style={[styles.actionText, { color: colors.secondary }]}>
              Share
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  feedCard: {
    borderRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
    overflow: 'hidden',
  },

  // Card Header
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },

  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },

  userInfo: {
    marginLeft: 8,
    flex: 1,
  },

  username: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 1,
  },

  timestamp: {
    fontSize: 10,
    opacity: 0.6,
  },

  // Privacy Badge
  privacyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    gap: 4,
  },

  privacyText: {
    fontSize: 10,
    fontWeight: '500',
  },

  // Card Content
  cardContent: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
    lineHeight: 20,
  },

  cardDescription: {
    fontSize: 12,
    lineHeight: 16,
    opacity: 0.9,
    marginBottom: 4,
  },

  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },

  locationText: {
    fontSize: 11,
    marginLeft: 4,
    opacity: 0.8,
  },

  // Visual Content
  visualContent: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },

  imageGrid: {
    flexDirection: 'row',
    height: Platform.OS === 'web' ? 200 : 200,
    gap: 3,
    justifyContent: 'space-between',
  },

  imageContainer: {
    flex: 1,
    aspectRatio: 1,
    borderRadius: Platform.OS === 'web' ? 12 : 12,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'rgba(0,0,0,0.05)',
    minHeight: 0,
    maxHeight: '100%',
  },

  feedImage: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    resizeMode: 'cover',
    flex: 1,
  },

  moreImagesOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  moreImagesText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },

  // Color Palette
  colorPalette: {
    flexDirection: 'row',
    height: 50,
    marginBottom: 8,
    borderRadius: 12,
    overflow: 'hidden',
  },

  colorSwatch: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  colorHex: {
    fontSize: 8,
    fontWeight: '600',
    color: 'white',
    textShadow: '0 1px 1px rgba(0,0,0,0.5)',
  },

  // Tags
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },

  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },

  tagText: {
    fontSize: 10,
    fontWeight: '500',
  },

  moreTagsText: {
    fontSize: 10,
    opacity: 0.6,
    alignSelf: 'center',
  },

  // Engagement Bar
  engagementBar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    gap: 20,
  },

  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
    gap: 4,
  },

  actionText: {
    fontSize: 10,
    fontWeight: '500',
  },
});

export default MoodBoardPreviewCard;
